import { apiClient } from '../client';
import type { ApiResponse, PaginatedResponse } from '../types';

// ---------------------------------------------------------------------------
// Document requests — CmClaimDocument rows, flattened across claims.
//
// Until this endpoint existed the table was reachable only one claim at a
// time, so the customer dashboard could say "88 documents you owe" with
// nowhere to go and see which.
// ---------------------------------------------------------------------------

/**
 * OPEN and PENDING are not two flavours of the same thing:
 *
 *   OPEN     the document is on the claim's checklist and nobody has asked
 *            the customer for it yet — nothing for them to do.
 *   PENDING  an adjuster requested it (which stamps `requestDate` and starts
 *            the reminder job) and is waiting on the upload.
 *   APPROVE  reviewed and accepted.
 *   REJECTED reviewed and sent back.
 *   CLOSE    no longer required.
 */
export type DocumentRequestStatus = 'OPEN' | 'PENDING' | 'APPROVE' | 'REJECTED' | 'CLOSE';

export interface DocumentRequestListItem {
  documentId: number;
  claimId: number;
  claimNumber: string | null;
  documentType: string | null;
  status: string;
  /** When an adjuster actually asked for it. Null while still OPEN. */
  requestDate: string | null;
  responseDate: string | null;
  creationDate: string;
  filename: string | null;
  comments: string | null;
  mandatory: boolean;
  /** Populated for admin/adjuster callers; customers already know who they are. */
  customerName: string | null;
}

export interface GetDocumentRequestsParams {
  status?: string;
  claimId?: number;
  /** ISO date string. */
  startDate?: string;
  /** ISO date string. */
  endDate?: string;
  /** Free text matched against claim number, document type and customer name. */
  search?: string;
  /** claimNumber | customer | documentType | status | requested. Defaults to requested. */
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  /** 0-indexed, matching the backend. */
  page?: number;
  pageSize?: number;
}

/**
 * GET /api/v1/Claims/GetDocumentRequests
 *
 * Scope comes from the caller's token, not from a parameter — a customer sees
 * their own claims' documents, an adjuster theirs, an admin everything. There
 * is deliberately no customerId argument to pass.
 *
 * Customers never receive rows flagged `onlyViewAdjuster`; the server filters
 * them out rather than relying on the client to hide them.
 */
export async function getDocumentRequests(
  params?: GetDocumentRequestsParams,
): Promise<PaginatedResponse<DocumentRequestListItem>> {
  const response = await apiClient.get<
    ApiResponse<PaginatedResponse<DocumentRequestListItem>>
  >('/api/v1/Claims/GetDocumentRequests', { params });
  return response.data.data;
}
